import { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { toJS } from 'mobx'

import { createCrudResourceContext } from '@/Core/UI/CrudResource/CrudResourceContextProvider'
import { ResourceList } from '@/Core/UI/CrudResource/ResourceList'
import { LocalFilterPanel, PushLocalFilterButtonToTitleExtra } from '@/Ship/Templates/CrudResource/localFilterPanel'

import { TaskResource } from '../../Resources/TaskResource'

import type { TaskFormContextLike } from '../../Contracts/Crud.types'

interface Props {
    formContext?: TaskFormContextLike
}

/**
 * Вкладка «Підзавдання» — таблиця ресурсу завдань, відфільтрована за
 * батьком (перенос легасі `TasksFormTemplate` → вкладений `TasksCrud` з
 * `prefilter={parent}` і create-формою з передзаповненим батьком).
 *
 * Відмінності від повної сторінки — точкові:
 *   • фільтр є, як у реєстрі (рядок статусів, лійка, панель, шестерня), але
 *     панель за замовчуванням згорнута й відкривається лише тут:
 *     `LocalFilterPanel` замість спільного для всіх списків `isFilterOpen`.
 *     Лічильники статусів і вибірка — лише серед підзавдань цього завдання
 *     (префільтр `parent`);
 *   • без заголовка (порожній фрагмент: `SetTitleFromResource` перекриває
 *     лише falsy-title, тож `false` тут не спрацював би);
 *   • без перемикача Таблиця/Канбан/Календар — `settings.taskViewModes:
 *     false` гейтить реєстрацію режимів у `TaskResource.AddTaskViewModes`.
 *
 * ⚠️ Шестерня зберігає колонки й поля фільтра на весь ресурс завдань: змінені
 * тут, вони змінюються і в реєстрі завдань, і навпаки. Замовник це прийняв.
 *
 * Кнопка «Додати» лишається: створення підзадачі з картки — легасі-канон,
 * `storages.addButton.initialValues.parent` = поточна задача. Свіжість
 * списку після повернення на вкладку забезпечує epoch-ремонт у TaskFormTemplate.
 */
export const TaskSubtasksTab = observer(({ formContext }: Props) => {
    const uuid = formContext?.values?.uuid
    const registryKey = `Task.Subtasks.${uuid ?? 'new'}.list`

    const crudContext = useMemo(() => {
        if (!uuid) return null
        const parent = toJS(formContext?.values)
        return createCrudResourceContext({
            contextType: 'list',
            registryKey,
            resource: TaskResource,
            prefilter: { parent },
            isFilter: true,
            settings: { taskViewModes: false },
            headerPage: {
                title: <></>,
                // Ядрова лійка перемикала б панель у всіх списках — див. `LocalFilterPanel`
                eventHandlers: { onAfterInit: { PushFilterButtonToTitleExtra: PushLocalFilterButtonToTitleExtra } },
            },
            storages: {
                // Канонічний ядровий шлях передзаповнення create-форми з кнопки
                // «Додати»: AddButton кличе actions.create(storages.addButton.initialValues)
                addButton: { initialValues: { parent } },
                filterPanel: new LocalFilterPanel(),
            },
            // Каст на межі нетипізованої .jsx-фабрики (пор. MyTasksPage):
            // resource/prefilter/... фабрика читає через ...props у рантаймі.
        } as unknown as Parameters<typeof createCrudResourceContext>[0])
        // Новий контекст лише на іншу задачу — значення форми стабільні в межах картки
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uuid])

    if (!crudContext) return null

    return <ResourceList crudContext={crudContext} registryKey={registryKey} />
})
